import React, { useState, useEffect } from 'react';
import { getAssistantManager } from '../utils/assistantManager';

const inputStyle = {
  backgroundColor: 'rgba(255,255,255,0.05)',
  border: '0.5px solid rgba(255,255,255,0.1)',
};

const cardStyle = {
  backgroundColor: 'rgba(255,255,255,0.04)',
  border: '0.5px solid rgba(255,255,255,0.08)',
};

const Settings = () => {
  const [settings, setSettings] = useState({
    assistantName: '',
    voice: 'alloy',
    model: 'gpt-4o',
    temperature: 0.7,
    knowledgeBase: '',
    firstMessage: 'Hello! How can I help you today?',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');

  const voiceOptions = [
    { value: 'alloy', label: 'Alloy (Neutral)' },
    { value: 'echo', label: 'Echo (Male)' },
    { value: 'fable', label: 'Fable (Male)' },
    { value: 'onyx', label: 'Onyx (Male)' },
    { value: 'nova', label: 'Nova (Female)' },
    { value: 'shimmer', label: 'Shimmer (Female)' },
  ];

  const modelOptions = [
    { value: 'gpt-4o', label: 'GPT-4o (Latest)' },
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini (Faster)' },
    { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
    { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' },
  ];

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const storedAgentData = localStorage.getItem('agentData');
      const storedUser = localStorage.getItem('user');
      let agentData = null;

      if (storedAgentData) {
        try { agentData = JSON.parse(storedAgentData); } catch {}
      } else if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          if (user.agent) agentData = user.agent;
        } catch {}
      }

      if (agentData) {
        setSettings({
          assistantName: agentData.agentName || agentData.name || '',
          voice: agentData.agentVoice || agentData.voice || 'alloy',
          model: 'gpt-4o',
          temperature: 0.7,
          knowledgeBase: agentData.systemPrompt || '',
          firstMessage: agentData.firstMessage || 'Hello! How can I help you today?',
        });
      } else {
        const savedSettings = localStorage.getItem('assistantSettings');
        if (savedSettings) setSettings(JSON.parse(savedSettings));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage('');
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const clientId = user.id || 'demo-client';
      const assistantManager = getAssistantManager(clientId);
      localStorage.setItem('assistantSettings', JSON.stringify(settings));
      const assistant = await assistantManager.getOrCreateAssistant(settings);
      setMessage(`Settings saved successfully! Assistant ID: ${assistant.id}`);
      setTimeout(() => setMessage(''), 5000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage('Error saving settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (field, value) => setSettings(prev => ({ ...prev, [field]: value }));

  const labelStyle = { color: 'rgba(255,255,255,0.55)' };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#07070f' }}>
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-purple-500/40 border-t-purple-400 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>Loading settings…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white p-4 md:p-6" style={{ backgroundColor: '#07070f' }}>
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="rounded-2xl p-5 md:p-6 mb-6" style={cardStyle}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-xl font-extrabold text-white tracking-tight">AI Assistant Settings</h1>
              <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.4)' }}>Configure your voice AI assistant</p>
            </div>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="w-full sm:w-auto px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: 'linear-gradient(135deg, #8B5CF6, #C084FC)' }}
            >
              {isSaving ? 'Saving…' : 'Save Settings'}
            </button>
          </div>
        </div>

        {/* Message banner */}
        {message && (
          <div
            className="mb-5 px-4 py-3 rounded-xl text-sm"
            style={
              message.includes('Error')
                ? { backgroundColor: 'rgba(239,68,68,0.08)', border: '0.5px solid rgba(239,68,68,0.25)', color: '#f87171' }
                : { backgroundColor: 'rgba(34,197,94,0.08)', border: '0.5px solid rgba(34,197,94,0.25)', color: '#4ade80' }
            }
          >
            {message}
          </div>
        )}

        {/* Basic Settings */}
        <div className="rounded-2xl mb-5" style={cardStyle}>
          <div className="px-6 py-4" style={{ borderBottom: '0.5px solid rgba(255,255,255,0.06)' }}>
            <h2 className="text-base font-semibold text-white">Basic Settings</h2>
            <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>Configure your assistant&apos;s basic behaviour</p>
          </div>
          <div className="p-6 space-y-5">
            <div>
              <label className="block text-xs font-medium mb-1.5" style={labelStyle}>Assistant Name</label>
              <input
                type="text"
                value={settings.assistantName}
                onChange={(e) => handleInputChange('assistantName', e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl text-sm text-white placeholder-white/25 focus:outline-none focus:ring-1 focus:ring-purple-500/50 transition"
                style={inputStyle}
                placeholder="My AI Assistant"
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1.5" style={labelStyle}>First Message</label>
              <textarea
                value={settings.firstMessage}
                onChange={(e) => handleInputChange('firstMessage', e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl text-sm text-white placeholder-white/25 focus:outline-none focus:ring-1 focus:ring-purple-500/50 transition"
                style={inputStyle}
                rows={3}
                placeholder="Hello! How can I help you today?"
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1.5" style={labelStyle}>Voice</label>
              <select
                value={settings.voice}
                onChange={(e) => handleInputChange('voice', e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl text-sm text-white focus:outline-none focus:ring-1 focus:ring-purple-500/50 transition"
                style={inputStyle}
              >
                {voiceOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium mb-1.5" style={labelStyle}>AI Model</label>
              <select
                value={settings.model}
                onChange={(e) => handleInputChange('model', e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl text-sm text-white focus:outline-none focus:ring-1 focus:ring-purple-500/50 transition"
                style={inputStyle}
              >
                {modelOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium mb-1.5" style={labelStyle}>
                Creativity Level: {settings.temperature}
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={settings.temperature}
                onChange={(e) => handleInputChange('temperature', parseFloat(e.target.value))}
                className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                style={{ background: `linear-gradient(to right, #8B5CF6 ${settings.temperature * 100}%, rgba(255,255,255,0.1) ${settings.temperature * 100}%)` }}
              />
              <div className="flex justify-between text-xs mt-1" style={{ color: 'rgba(255,255,255,0.3)' }}>
                <span>Focused</span>
                <span>Creative</span>
              </div>
            </div>
          </div>
        </div>

        {/* Knowledge Base */}
        <div className="rounded-2xl" style={cardStyle}>
          <div className="px-6 py-4" style={{ borderBottom: '0.5px solid rgba(255,255,255,0.06)' }}>
            <h2 className="text-base font-semibold text-white">Knowledge Base</h2>
            <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>Information about your business to help your assistant answer questions</p>
          </div>
          <div className="p-6">
            <textarea
              value={settings.knowledgeBase}
              onChange={(e) => handleInputChange('knowledgeBase', e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl text-sm text-white placeholder-white/25 focus:outline-none focus:ring-1 focus:ring-purple-500/50 transition"
              style={inputStyle}
              rows={8}
              placeholder={`Enter information about your business, products, services, policies, etc.\n\nExample:\n- We are a software company specialising in AI solutions\n- Our main product is a voice AI assistant for businesses\n- We offer 24/7 customer support\n- Our pricing starts at $99/month`}
            />
            <p className="mt-2 text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
              This information is added to your assistant&apos;s system prompt to help it understand your business.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
